import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../../config/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import { logout, setViewAs, ADMIN_UIDS } from "../Auth/authSlice";

const TEST_UID = "xXN10FCqSHZlkuHNqR5L6ia1jDv1";

// Ouvre IMMOMED (etsmed.com/immo) dans un nouvel onglet avec un spinner
// affiché immédiatement : le premier chargement passe par la fonction
// immoProxy (démarrage à froid possible) avant que la page n'arrive.
function openImmomed() {
  const w = window.open("", "_blank");
  if (!w) {
    window.open("/immo/", "_blank");
    return;
  }
  w.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>IMMOMED</title><style>
body{margin:0;height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;background:#fff;font-family:system-ui,-apple-system,sans-serif;color:#111827}
.s{width:44px;height:44px;border:4px solid #e5e7eb;border-top-color:#111827;border-radius:50%;animation:r .8s linear infinite}
@keyframes r{to{transform:rotate(360deg)}}
</style></head><body><div class="s"></div><div>Chargement d'IMMOMED…</div></body></html>`);
  w.document.close();
  w.location.href = "/immo/";
}

export default function AdminMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, isAdmin, displayName } = useSelector((state) => state.auth);
  const isGestockOnly = role === "gestock";
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Save current page when navigating
  useEffect(() => {
    if (location.pathname !== "/" && location.pathname !== "/login") {
      localStorage.setItem("lastVisitedPage", location.pathname);
    }
  }, [location]);

  useEffect(() => {
    if (isGestockOnly) {
      // Gestock-only users don't see the station list.
      setStations([]);
      setLoading(false);
      return;
    }
    async function load() {
      const snap = await getDocs(collection(db, "users"));
      const list = [];
      const seenStations = new Set();
      snap.forEach((doc) => {
        const d = doc.data();
        // skip admin accounts and gestock account
        if (ADMIN_UIDS.includes(doc.id)) return;
        if (doc.id.startsWith("test_")) return;

        const stationKey = String(d.station || "")
          .trim()
          .toUpperCase();
        if (!stationKey || seenStations.has(stationKey)) return;
        seenStations.add(stationKey);

        list.push({
          uid: doc.id,
          station: d.station,
          ville: d.ville,
          quin: d.quin || null,
        });
      });
      list.sort((a, b) => (a.station || "").localeCompare(b.station || ""));
      setStations(list);
      setLoading(false);
    }
    load();
  }, [isGestockOnly]);

  const handleSelect = (user) => {
    dispatch(
      setViewAs({ uid: user.uid, station: user.station, quin: user.quin }),
    );
    navigate("/dashboard");
  };

  const handleSelectGestock = () => {
    // Admin enters Gestock as themselves (rules now check isAdmin instead of a fixed UID).
    dispatch(setViewAs({ uid: null, station: "GESTOCK", quin: null }));
    localStorage.removeItem("viewAsUid");
    localStorage.setItem("viewAsStation", "GESTOCK");
    localStorage.removeItem("viewAsQuin");
    navigate("/dashboard");
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  return (
    <div style={styles.root} className="admin-menu-root">
      <div style={styles.card} className="admin-menu-card">
        <div style={styles.header}>
          <div style={styles.logo}>ETS MED</div>
          {displayName && (
            <div style={styles.profileName}>👤 {displayName}</div>
          )}
        </div>

        {loading ? (
          <div style={styles.loading}>Chargement…</div>
        ) : (
          <div style={styles.grid} className="admin-menu-grid">
            {!isGestockOnly &&
              stations.map((u) => (
                <button
                  key={u.uid}
                  style={styles.stationBtn}
                  onClick={() => handleSelect(u)}
                >
                  <span style={styles.stationIcon}>⛽</span>
                  <span style={styles.stationName}>{u.station}</span>
                  {u.ville && (
                    <span style={styles.stationVille}>{u.ville}</span>
                  )}
                  {u.quin && <span style={styles.quinBadge}>{u.quin}</span>}
                </button>
              ))}
            {!isGestockOnly && (
              <button
                style={{ ...styles.stationBtn, ...styles.testBtn }}
                onClick={() => {
                  dispatch(
                    setViewAs({ uid: TEST_UID, station: "TEST", quin: "QT" }),
                  );
                  navigate("/dashboard");
                }}
              >
                <span style={styles.stationIcon}>🧪</span>
                <span style={styles.stationName}>TEST</span>
                <span style={styles.stationVille}>zackdvlp</span>
                <span style={styles.quinBadge}>QT</span>
              </button>
            )}
            <button
              style={{ ...styles.stationBtn, ...styles.gestockBtn }}
              onClick={handleSelectGestock}
            >
              <span style={styles.stationIcon}>📦</span>
              <span style={styles.stationName}>GESTOCK</span>
              <span style={styles.stationVille}>Stock carburant</span>
            </button>
            {isAdmin && (
              <button
                style={{ ...styles.stationBtn, ...styles.validateBtn }}
                onClick={() => navigate("/validate")}
              >
                <span style={styles.stationIcon}>✅</span>
                <span style={styles.stationName}>VALIDATION</span>
                <span style={styles.stationVille}>Indices & versements</span>
              </button>
            )}
            {(isAdmin || role === "gestock") && (
              <button
                style={{ ...styles.stationBtn, ...styles.correctionBtn }}
                onClick={() => navigate("/correction")}
              >
                <span style={styles.stationIcon}>✏️</span>
                <span style={styles.stationName}>CORRECTION</span>
                <span style={styles.stationVille}>Corriger les saisies</span>
              </button>
            )}
            <button
              style={{ ...styles.stationBtn, ...styles.immomedBtn }}
              onClick={openImmomed}
            >
              <span style={styles.stationIcon}>🏠</span>
              <span style={styles.stationName}>IMMOMED</span>
              <span style={styles.stationVille}>Immobilier MED</span>
            </button>
          </div>
        )}

        <button style={styles.logoutBtn} onClick={handleLogout}>
          🚪 Déconnexion
        </button>
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh",
    background: "#0f172a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    background: "#1e293b",
    borderRadius: 16,
    padding: "40px 32px",
    width: "100%",
    maxWidth: 560,
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
  },
  header: {
    textAlign: "center",
    marginBottom: 32,
  },
  logo: {
    fontSize: 28,
    fontWeight: 800,
    color: "#e2e8f0",
    letterSpacing: 2,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 13,
    color: "#64748b",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  profileName: {
    marginTop: 12,
    fontSize: 13,
    color: "#94a3b8",
    fontWeight: 600,
  },
  loading: {
    textAlign: "center",
    color: "#64748b",
    padding: 32,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 24,
  },
  stationBtn: {
    background: "#0f172a",
    border: "1px solid #334155",
    borderRadius: 12,
    padding: "16px 12px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    transition: "border-color .15s, background .15s",
    color: "#e2e8f0",
  },
  gestockBtn: {
    borderColor: "#0ea5e9",
  },
  validateBtn: {
    borderColor: "#22c55e",
  },
  correctionBtn: {
    borderColor: "#a855f7",
  },
  immomedBtn: {
    borderColor: "#14b8a6",
  },
  testBtn: {
    borderColor: "#f59e0b",
  },
  stationIcon: {
    fontSize: 28,
    marginBottom: 4,
  },
  stationName: {
    fontSize: 15,
    fontWeight: 700,
    color: "#e2e8f0",
    letterSpacing: 1,
  },
  stationVille: {
    fontSize: 11,
    color: "#64748b",
  },
  quinBadge: {
    fontSize: 10,
    background: "#0ea5e920",
    color: "#0ea5e9",
    border: "1px solid #0ea5e940",
    borderRadius: 4,
    padding: "1px 6px",
    marginTop: 2,
  },
  logoutBtn: {
    width: "100%",
    padding: "12px",
    background: "transparent",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "#ef4444",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
};
